import { useMutation } from '@tanstack/react-query';

import { useTrackInvitedLibraryMember } from '@air/analytics';
import { Libraries } from '@air/api';
import { type Library } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { getLibraryMembersKey } from '~/components/LibraryBeta/hooks/useLibraryMembers';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { queryClient } from '~/swr-hooks/queryClient';

type TargetLibrary = {
  libraryId: Library['id'];
  roleId: string;
};

type EmailLibraryAttempt = TargetLibrary & {
  email: string;
};

type FailedEmailLibraryAttempt = EmailLibraryAttempt & {
  reason: unknown;
};

type SanitizedFailure = {
  libraryId: Library['id'];
  roleId: string;
  errorName: string;
  errorMessage: string;
};

const sanitizeFailure = ({ libraryId, roleId, reason }: FailedEmailLibraryAttempt): SanitizedFailure => {
  if (reason instanceof Error) {
    return { libraryId, roleId, errorName: reason.name, errorMessage: reason.message };
  }

  if (typeof reason === 'string') {
    return { libraryId, roleId, errorName: 'Unknown', errorMessage: reason };
  }

  return { libraryId, roleId, errorName: 'Unknown', errorMessage: 'Non-Error rejection' };
};

export type AddMembersToLibrariesInput = {
  emails: string[];
  libraries: TargetLibrary[];
};

export type AddMembersToLibrariesResult = {
  succeededAttempts: EmailLibraryAttempt[];
  failedAttempts: FailedEmailLibraryAttempt[];
  totalEmails: number;
  totalLibraries: number;
};

export const useAddMembersToLibraries = () => {
  const { trackInvitedLibraryMember } = useTrackInvitedLibraryMember();
  const { currentWorkspace } = useCurrentWorkspace();

  const addMembersToLibraries = useMutation({
    mutationFn: async ({ emails, libraries }: AddMembersToLibrariesInput): Promise<AddMembersToLibrariesResult> => {
      const workspaceId = currentWorkspace?.id;

      if (!workspaceId) {
        throw new Error('No workspaceId');
      }

      const attempts: EmailLibraryAttempt[] = emails.flatMap((email) =>
        libraries.map(({ libraryId, roleId }) => ({ email, libraryId, roleId })),
      );

      const results = await Promise.allSettled(
        attempts.map(({ email, libraryId, roleId }) =>
          Libraries.addMembers({ workspaceId, libraryId, members: [{ email, roleId }] }).then(() => ({
            email,
            libraryId,
            roleId,
          })),
        ),
      );

      const succeededAttempts: EmailLibraryAttempt[] = [];
      const failedAttempts: FailedEmailLibraryAttempt[] = [];

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          succeededAttempts.push(result.value);
        } else {
          failedAttempts.push({ ...attempts[index], reason: result.reason });
        }
      });

      succeededAttempts.forEach(({ email, libraryId, roleId }) => {
        trackInvitedLibraryMember({
          library_id: libraryId,
          library_member_email: email,
          library_member_role_id: roleId,
        });
      });

      const succeededLibraryIds = new Set(succeededAttempts.map((s) => s.libraryId));
      succeededLibraryIds.forEach((libraryId) => {
        queryClient.invalidateQueries({ queryKey: getLibraryMembersKey(libraryId) });
      });

      if (failedAttempts.length > 0) {
        reportErrorToBugsnag({
          error: new Error(`Failed to add ${failedAttempts.length} of ${attempts.length} library memberships`),
          context: 'Failed to add members to libraries',
          metadata: { failures: failedAttempts.map(sanitizeFailure) },
        });
      }

      if (succeededAttempts.length === 0) {
        throw new Error('Failed to add any members to libraries');
      }

      return {
        succeededAttempts,
        failedAttempts,
        totalEmails: emails.length,
        totalLibraries: libraries.length,
      };
    },
  });

  return { addMembersToLibraries };
};
